import { z } from "zod"
import type { Friend } from "@/entities/friend"
import type { User } from "@/entities/user"
import type { CharacterLastMessageResponse } from "@/shared/api/generated-backend"
import {
	zCharacterLastMessageResponse,
	zCharacterSlug,
} from "@/shared/api/generated-backend/zod.gen"
import { registerSessionCleanup } from "@/shared/lib/auth"
import { createSessionStorageItem } from "@/shared/lib/storage"

const VERSION = 5
const MAX_AGE_MS = 7 * 24 * 60 * 60_000

let generation = 0

const UserCoreSchema = z.object({
	id: z.number(),
	luni_name: z.string(),
	character: zCharacterSlug,
})

const SnapshotSchema = z.object({
	version: z.literal(VERSION),
	owner: z.string(),
	savedAt: z.number(),
	user: z.custom<User>((value) => UserCoreSchema.safeParse(value).success),
	friends: z.custom<Friend[]>(Array.isArray),
	// Превью user.character: персонаж тот же, что в записи user.
	lastMessage: zCharacterLastMessageResponse.nullable(),
})

const stored = createSessionStorageItem({
	key: "boot-snapshot",
	schema: SnapshotSchema.nullable(),
	fallback: null,
})

export interface Snapshot {
	owner: string
	user: User
	friends: Friend[]
	lastMessage: CharacterLastMessageResponse | null
}

export function loadSnapshot(): Snapshot | null {
	// Другая VERSION или битый JSON дают null — их перезапишет следующий save.
	const snapshot = stored.get()
	if (snapshot && Date.now() - snapshot.savedAt > MAX_AGE_MS) {
		dropSnapshot()
		return null
	}
	return snapshot
}

export function saveSnapshot({
	owner,
	user,
	friends,
	lastMessage,
}: Snapshot): void {
	// Не записался — старый снимок с устаревшими данными не должен подняться.
	const saved = stored.set({
		version: VERSION,
		owner,
		savedAt: Date.now(),
		// token в типе User нет, но в объекте с сервера он может быть.
		user: { ...user, token: null } as User,
		friends,
		lastMessage,
	})
	if (!saved) dropSnapshot()
}

export function dropSnapshot(): void {
	generation += 1
	stored.remove()
}

export function snapshotGeneration(): number {
	return generation
}

registerSessionCleanup(dropSnapshot)
